import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, PageShell } from "@/components/app/page";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { productsAPI } from "@/services/api";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/errorHelpers";
import { getProductSubcategories, PRODUCT_CATEGORIES } from "@/utils/productCategories";

export default function ProductSubmitPage({ institutional = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    author_name: "",
    product_type: "texte_simple",
    category: "",
    subcategory: "",
    price_mc: "",
    summary: "",
    author_about: "",
    rights_basis: "",
    rights_reference: "",
    institutional_access_scope: "public",
  });
  const [file, setFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [rightsProofFile, setRightsProofFile] = useState(null);
  const subcategories = getProductSubcategories(formData.category);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.category || !formData.subcategory) {
      toast.error("Choisissez une categorie et une sous-categorie.");
      return;
    }
    if (institutional && (!file || !rightsProofFile || !formData.rights_basis || !formData.rights_reference.trim())) {
      toast.error("Le fichier, le fondement des droits et sa preuve sont obligatoires.");
      return;
    }
    setLoading(true);

    try {
      const payload = {
        ...formData,
        price_mc: institutional ? 0 : (formData.price_mc === "" || formData.price_mc === null ? undefined : Number(formData.price_mc)),
      };
      const response = institutional ? await productsAPI.createInstitutional(payload) : await productsAPI.create(payload);
      const newProductId = response.data.id;

      if (coverFile) {
        await productsAPI.uploadCover(newProductId, coverFile);
      }

      if (file) {
        await productsAPI.uploadFile(newProductId, file);
      }
      if (institutional && rightsProofFile) {
        await productsAPI.uploadRightsProof(newProductId, rightsProofFile);
      }

      toast.success(institutional ? "Oeuvre libre soumise. Un autre administrateur doit la valider." : "Produit soumis avec succès. En attente de validation.");
      setFormData({ title: "", description: "", author_name: "", product_type: "texte_simple", category: "", subcategory: "", price_mc: "", summary: "", author_about: "", rights_basis: "", rights_reference: "", institutional_access_scope: "public" });
      setFile(null);
      setCoverFile(null);
      setRightsProofFile(null);
      navigate("/dashboard");
    } catch (error) {
      toast.error(getErrorMessage(error, "Erreur lors de la soumission"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageShell data-testid="product-submit-page">
      <PageHeader title={institutional ? "Déposer une oeuvre libre" : "Soumettre un produit"} description={institutional ? "Dépôt institutionnel gratuit avec preuve des droits de diffusion." : "Soumettez votre oeuvre pour validation par la commission MMJ"} />

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">Informations du produit</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="title">Titre</Label>
                <Input
                  id="title"
                  data-testid="product-title-input"
                  value={formData.title}
                  onChange={(event) => setFormData({ ...formData, title: event.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="product-description-input"
                  value={formData.description}
                  onChange={(event) => setFormData({ ...formData, description: event.target.value })}
                  required
                  rows={4}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="author_name">Nom de l'auteur</Label>
                <Input
                  id="author_name"
                  value={formData.author_name}
                  onChange={(event) => setFormData({ ...formData, author_name: event.target.value })}
                  placeholder="Laissez vide pour utiliser votre nom de partenaire"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="summary">Resume du produit</Label>
                <Textarea
                  id="summary"
                  value={formData.summary}
                  onChange={(event) => setFormData({ ...formData, summary: event.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="author_about">A propos de l'auteur</Label>
                <Textarea
                  id="author_about"
                  value={formData.author_about}
                  onChange={(event) => setFormData({ ...formData, author_about: event.target.value })}
                  rows={3}
                  className="mt-1"
                />
              </div>

              {institutional && (
                <div className="space-y-4 rounded-xl border border-[#D7E7DE] bg-[#F7FBF8] p-4">
                  <div>
                    <p className="text-sm font-semibold text-[#2A4B46]">Droits et diffusion</p>
                    <p className="mt-1 text-xs leading-5 text-[#5C625F]">Ces informations sont vérifiées par un autre administrateur avant publication.</p>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="rights_basis">Fondement des droits</Label>
                      <Select value={formData.rights_basis} onValueChange={(value) => setFormData({ ...formData, rights_basis: value })}>
                        <SelectTrigger className="mt-1"><SelectValue placeholder="Choisir" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public_domain">Domaine public</SelectItem>
                          <SelectItem value="creative_commons">Licence Creative Commons</SelectItem>
                          <SelectItem value="authorized_distribution">Autorisation de diffusion</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="institutional_access_scope">Accès</Label>
                      <Select value={formData.institutional_access_scope} onValueChange={(value) => setFormData({ ...formData, institutional_access_scope: value })}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public, sans compte</SelectItem>
                          <SelectItem value="authenticated">Utilisateurs connectés</SelectItem>
                          <SelectItem value="active_par">Partenaires PAR actifs</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="rights_reference">Source ou référence des droits</Label>
                    <Textarea id="rights_reference" value={formData.rights_reference} onChange={(event) => setFormData({ ...formData, rights_reference: event.target.value })} rows={3} placeholder="Lien officiel, numéro de contrat ou référence de licence" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="rights_proof">Preuve des droits</Label>
                    <Input id="rights_proof" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(event) => setRightsProofFile(event.target.files[0])} className="mt-1" />
                    <p className="mt-1 text-xs text-[#5C625F]">PDF ou image, conservé en privé pour la commission MMJ.</p>
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="product_type">Type de produit</Label>
                <Select
                  value={formData.product_type}
                  onValueChange={(value) => setFormData({ ...formData, product_type: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="product-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="texte_simple">Documents textes</SelectItem>
                    <SelectItem value="texte_sonorise">Fichier audio</SelectItem>
                    <SelectItem value="texte_chante">Fichier video</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="category">Categorie</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value, subcategory: "" })}
                  >
                    <SelectTrigger className="mt-1" data-testid="product-category-select">
                      <SelectValue placeholder="Choisir une categorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {PRODUCT_CATEGORIES.map((category) => (
                        <SelectItem key={category.value} value={category.value}>{category.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="subcategory">Sous-categorie</Label>
                  <Select
                    value={formData.subcategory}
                    onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                    disabled={!formData.category}
                  >
                    <SelectTrigger className="mt-1" data-testid="product-subcategory-select">
                      <SelectValue placeholder="Choisir une sous-categorie" />
                    </SelectTrigger>
                    <SelectContent>
                      {subcategories.map((subcategory) => (
                        <SelectItem key={subcategory} value={subcategory}>{subcategory}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {!institutional && <div>
                <Label htmlFor="price_mc">Valeur (Points-Monnaie)</Label>
                <Input
                  id="price_mc"
                  type="number"
                  step="0.01"
                  min="0.1"
                  data-testid="product-price-input"
                  value={formData.price_mc}
                  onChange={(event) => setFormData({ ...formData, price_mc: event.target.value })}
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-[#5C625F]">Laissez vide pour un produit libre d'accès aux partenaires PAR actifs.</p>
              </div>}

              <div>
                <Label htmlFor="file">Fichier du produit</Label>
                <Input
                  id="file"
                  type="file"
                  data-testid="product-file-input"
                  onChange={(event) => setFile(event.target.files[0])}
                  accept=".pdf,.mp3,.mp4"
                  required={institutional}
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-[#5C625F]">Formats acceptes : PDF, MP3 ou MP4.</p>
              </div>

              <div>
                <Label htmlFor="cover">Image de couverture</Label>
                <Input
                  id="cover"
                  type="file"
                  data-testid="product-cover-input"
                  onChange={(event) => setCoverFile(event.target.files[0])}
                  accept=".jpg,.jpeg,.png,.webp"
                  className="mt-1"
                />
                <p className="mt-1 text-xs text-[#5C625F]">Cette image sera automatiquement utilisee comme miniature dans le catalogue, les fiches et la bibliotheque.</p>
              </div>

              <Button type="submit" data-testid="submit-product-button" disabled={loading} className="w-full bg-[#2A4B46] text-white hover:bg-[#1f3a36]">
                {loading ? "Soumission en cours..." : institutional ? "Soumettre l'oeuvre libre" : "Soumettre le produit"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#2A4B46] font-['Outfit']">Contraintes a respecter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm text-[#5C625F]">
              <Guideline title="Valeurs" text="Qualité culturelle, littéraire et respect des valeurs de la plateforme." />
              <Guideline title="Documents textes" text="Fichier PDF lisible et complet." />
              <Guideline title="Fichier audio" text="Fichier audio au format accepte par la plateforme." />
              <Guideline title="Fichier video" text="Fichier video au format accepte par la plateforme." />
              <Guideline title="Validation" text={institutional ? "Un autre administrateur vérifie les droits, la preuve et le contenu avant publication." : "Tous les produits sont analyses par la commission MMJ."} />
            </div>
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}

function Guideline({ title, text }) {
  return (
    <div className="rounded-2xl bg-[#F9F7F4] p-3">
      <p className="mb-1 font-semibold text-[#2A4B46]">{title}</p>
      <p>{text}</p>
    </div>
  );
}
